import axios, { AxiosPromise, Canceler } from 'axios';
import ViewUI from 'view-design';
import router from '@/router/index';
import config from '@/config/index';
import {
    clearLoginStatus, localRead, isNotLoginPage, getShareChannelAccessToken,
    readSSOInfo, isSSO, goToRedirectPage,
} from '@/libs/util';
import {
    debounceExitShare, genSign,
} from '@/libs/tools';

const { CancelToken } = axios;
const networkReport = {
    count: 0,
    msg: '',
    url: '',
};

let showLogoutMessage = false;

const SECRET = process.env.VUE_APP_SECRET as string;

function reportNetworkErr() {
    // 判断有没有错误上报
    if (networkReport.msg) {
        router.app.$log('WEB_NETWORK', 'ERROR', { data: {
            msg: networkReport.msg,
            count: networkReport.count,
            reqUrl: networkReport.url,
            page: window.location.href,
        } });
        networkReport.msg = '';
    }
}

export interface HttpReq<T> extends AxiosPromise<T> {
    cancel: Canceler;
}
class HttpRequest {
    public baseUrl: string;

    public queue: any;

    public pendingRequests!: {[x in string]: Canceler};

    public source: string = '';

    constructor(baseUrl: string, source = 'api') {
        this.baseUrl = baseUrl;
        this.queue = {};
        this.pendingRequests = {};
        this.source = source;
    }

    public getInsideConfig(options: any) {
        const token = localRead(config.tokenKey);
        const headers: any = {
            'client-type': 'pc',
        };
        if (token) {
            headers.Cpl = token;
        }
        // 分享链接的token
        if (options.shareAccessToken) {
            const { shareId } = router.app.$route.params;
            const shareAccessToken = getShareChannelAccessToken(shareId);
            headers['Share-Access-Token'] = shareAccessToken || '';
        }

        if (this.source === 'ws') {
            delete headers['client-type'];
        }

        if (this.source === 'api' && isSSO()) {
            const { access_token } = readSSOInfo();
            headers['external-token'] = access_token || '';
        }

        if (['ws', 'log'].includes(this.source)) {
            delete headers.Cpl;
        }

        const reqConfig = {
            // baseURL: options.mock ? config.baseUrl.mock : this.baseUrl,
            baseURL: this.baseUrl,
            transformRequest: [
                (oldData: any) => {
                    let newStr = '';
                    if (oldData) {
                        Object.keys(oldData).map((item) => {
                            if (item) {
                                newStr += `${encodeURIComponent(item)}=${encodeURIComponent(
                                    oldData[item],
                                )}&`;
                            }
                            return newStr;
                        });
                        newStr = newStr.slice(0, -1);
                    }
                    return newStr;
                },
            ],
            headers,
            withCredentials: true,
        };
        return reqConfig;
    }

    public getUploadConfig(options: any) {
        const token = localRead(config.tokenKey);
        const headers: any = {
            'Content-Type': 'multipart/form-data',
            'client-type': 'pc',
        };
        if (token) {
            headers.Cpl = token;
        }
        const reqConfig = {
            baseURL: options.baseUrl || this.baseUrl,
            transformRequest: [
                (oldData: any) => {
                    const params = new FormData();
                    if (oldData) {
                        Object.keys(oldData).map((item: string) => {
                            if (item) {
                                params.append(item, oldData[item]);
                            }
                            return item;
                        });
                    }
                    return params;
                },
            ],
            headers,
            withCredentials: true,
            onUploadProgress(event: any) {
                if (options.onProgress && event) {
                    event.percent = Math.round((event.loaded * 100) / event.total);
                    options.onProgress(event);
                }
            },
        };
        return reqConfig;
    }

    public destroy(url: string) {
        delete this.queue[url];
        if (!Object.keys(this.queue).length) {
            // Spin.hide()
        }
    }

    public interceptors(instance: any, opt: any) {
        const url = opt.url;
        // 请求拦截
        instance.interceptors.request.use(
            (reqConfig: any) => {
                // 添加全局的loading...
                if (!Object.keys(this.queue).length) {
                    // Spin.show() // 不建议开启，因为界面不友好
                }
                this.queue[url] = true;
                return reqConfig;
            },
            (error: any) => Promise.reject(error),
        );
        // 响应拦截
        instance.interceptors.response.use(
            (res: any) => {
                delete this.pendingRequests[res.config.url];

                this.destroy(url);

                const { data, status, config: resConfig } = res;

                if (data.code > 100000) {
                    ViewUI.Message.destroy();
                }

                let msg: any;
                if (data.code === 14107) {
                    msg = router.app.$t(`errorCode.${data.code}`, { code: data?.data?.error?.code || 0, errorMessage: data?.data?.error?.message || 'errorCode.errorUnknown' });
                } else {
                    msg = router.app.$t(`errorCode.${data.code}`);
                }

                // 网络错误上报
                reportNetworkErr();

                // SSO 登录失效相关处理
                if ([1001, 4006].includes(data.code)) {
                    // 第三方登录直接重定向
                    if (isSSO()) {
                        goToRedirectPage();
                        clearLoginStatus();
                        return Promise.reject(data);
                    }
                }

                if (data.code === 1001) {
                    if (
                        router.app.$route.name &&
                        router.app.$route.name &&
                        !isNotLoginPage(router.app.$route.name)
                    ) {
                        ViewUI.Message.error({
                            content: msg,
                            duration: 3,
                            onClose() {
                                const { origin, href } = window.location;
                                window.location.href = `${origin}/login?url=${escape(href)}`;
                            },
                        });
                    }
                    clearLoginStatus();
                    return Promise.reject(data);
                }

                // 没有项目权限 或者 项目被删
                if ([15000, 6604, 6605].includes(data.code)) {
                    router.push({
                        name: 'noProject',
                    });
                    return Promise.reject(data);
                }

                if ([7705, 7905, 7955, 4502].includes(data.code)) {
                    return Promise.reject(data);
                }

                // 异常错误码：2002 1040 1203
                if ([1040, 1203, 2002].includes(data.code)) {
                    router.app.$log('WEB_NETWORK', 'ERROR_CODE', { data: { ...data, url: resConfig.url } });
                    return Promise.reject(data);
                }

                if ([7159].includes(data.code)) {
                    router.app.$log('WEB_NETWORK', 'CODE_UNKNOWN', { data: { ...data, url: resConfig.url } });
                    return Promise.reject(data);
                }

                // 如果是自定义提示
                if (opt.customTips) {
                    if (data.code === 0) {
                        return { data, status };
                    }
                    return Promise.reject(data);
                }

                // 后端导入超过限制
                if ([1].includes(data.code) && data.msg.indexOf('Allowed memory size of') === 0) {
                    ViewUI.Modal.error({
                        title: router.app.$t('base.tipText'),
                        content: router.app.$t('errorCode.importLimit'),
                    });
                    router.app.$log('WEB_NETWORK', 'ERROR_IMPORT_LIMIT', { data: { ...data, url: resConfig.url } });
                    return Promise.reject(data);
                }

                // faq 知识库
                if ([6702, 6708].includes(data.code)) {
                    msg = router.app.$t(`errorCode.${data.code}`, { title: data.data.question });
                    ViewUI.Message.error({
                        background: true,
                        closable: true,
                        content: msg,
                        duration: 6,
                    });
                    return Promise.reject(data);
                }

                // token管理
                if (data.code >= 8501 && data.code <= 8518) {
                    return Promise.reject(data);
                }

                // 模板
                if (data.code >= 6100 && data.code <= 6101) {
                    return Promise.reject(data);
                }

                // 聊天插件保存
                if (data.code >= 6300 && data.code <= 6303) {
                    return Promise.reject(data);
                }

                // 分享链接认证失败或过期
                if ([48001, 48002, 48003].includes(data.code)) {
                    const currentName = router.app.$route.name;
                    debounceExitShare(router, data.code, currentName as string);
                    return Promise.reject(data);
                }

                if ([100014].includes(data.code)) {
                    ViewUI.Modal.error({
                        title: router.app.$t('base.tipText'),
                        render(h: any) {
                            return h(
                                'div',
                                {
                                    attrs: {
                                        class: 'modal-block',
                                    },
                                },
                                [
                                    h('p', router.app.$t(`errorCode.${data.code}`)),
                                    h(
                                        'a',
                                        {
                                            attrs: {
                                                href: 'https://help.adspower.io/?page_id=825',
                                                target: '_blank',
                                            },
                                        },
                                        router.app.$t('showMore'),
                                    ),
                                ],
                            );
                        },
                    });
                    return Promise.reject(data);
                }

                if ([100015].includes(data.code)) {
                    ViewUI.Modal.info({
                        title: router.app.$t('base.tipText'),
                        render(h: any) {
                            return h(
                                'div',
                                {
                                    attrs: {
                                        class: 'modal-block',
                                    },
                                },
                                [
                                    h(
                                        'p',
                                        router.app.$t(`errorCode.${data.code}`, {
                                            num: data.data.num,
                                        }),
                                    ),
                                ],
                            );
                        },
                    });
                    return Promise.reject(data);
                }

                if ([4203, 4204, 4205, 4205, 4206].includes(data.code)) {
                    const content = router.app.$t(`errorCode.${data.code}`);
                    const lineCon = router.app.$t('errorCode.line', { m: data.data.line });
                    ViewUI.Modal.error({
                        title: router.app.$t('base.tipText'),
                        content: `${lineCon}${content}`,
                    });
                    return Promise.reject(data);
                }

                if ([20000, 3030, 3031].includes(data.code)) {
                    ViewUI.Message.error({
                        background: true,
                        render(h: any) {
                            return h('p', router.app.$t(`errorCode.${data.code}`));
                        },
                    });
                    return Promise.reject(data);
                }

                if ([12000, 12001, 12002].includes(data.code)) {
                    ViewUI.Modal.error({
                        title: router.app.$t('base.tipText'),
                        content: router.app.$t(`errorCode.${data.code}`),
                    });
                    return Promise.reject(data);
                }

                if (data.code === 6500) {
                    msg = data.msg || '';
                    const time = msg.split(' ');
                    const num = time[time.length - 2] || '';

                    ViewUI.Message.error({
                        background: true,
                        render(h: any) {
                            return h(
                                'p',
                                router.app.$t(`errorCode.${data.code}`, {
                                    num,
                                }),
                            );
                        },
                    });
                    return Promise.reject(data);
                }

                if ([7110].includes(data.code)) {
                    ViewUI.Message.error({
                        background: true,
                        closable: true,
                        content: router.app.$t(`errorCode.${data.code}`, {
                            projectName: data.data.project_name,
                        }).toString(),
                        duration: 6,
                    });
                    return Promise.reject(data);
                }

                if (msg.indexOf('errorCode.') === 0 || data.code === 1000 || data.code === 1002) {
                    if (msg.indexOf('errorCode.') === 0) {
                        msg = `${router.app.$t(`errorCode.${data.code}`)} (${data.code || '0000'})`;
                    }
                    setTimeout(() => {
                        if (msg.indexOf('cURL error') > -1) {
                            ViewUI.Modal.error({
                                title: router.app.$t('base.tipText'),
                                render(h: any) {
                                    return h(
                                        'div',
                                        {
                                            attrs: {
                                                class: 'modal-block',
                                            },
                                        },
                                        [
                                            h('p', `ㄟ(▔▽▔)ㄏ ${router.app.$t('errorCode.error')}`),
                                            h('p', `${router.app.$t('errorCode.error1')}(${status})`),
                                        ],
                                    );
                                },
                            });
                            // 未知错误码上报
                            if (this.source === 'api') {
                                router.app.$log('WEB_NETWORK', 'CODE_UNKNOWN_STATUS', { data: { ...data, url: resConfig.url, status } });
                            }
                        } else {
                            // 抛出未知异常
                            ViewUI.Modal.error({
                                title: msg,
                                content: router.app.$t('errorCode.unknown'),
                            });
                            // 未知错误码上报
                            router.app.$log('WEB_NETWORK', 'CODE_UNKNOWN', { data: { ...data, url: resConfig.url } });
                        }
                    }, 300);
                    return Promise.reject(data);
                }

                switch (data.code) {
                    case 8606:
                        ViewUI.Modal.warning({
                            title: router.app.$t('base.tipText'),
                            content: router.app.$t(`errorCode.${data.code}`),
                        });
                        return Promise.reject(data);
                    // fb广告账号绑定信用卡
                    case 5200:
                        return Promise.reject(data);
                    default:
                        if (data.code === 0) {
                            return { data, status };
                        }

                        // 广告管理-同步数据
                        // 3016 启用渠道失败 跳过提示
                        if ([5100, 5101, 5102, 5103, 8605, 3016].includes(data.code)) {
                            return Promise.reject(data);
                        }

                        if ([8604, 8607, 8620].includes(data.code)) {
                            const hrefArr: any = {
                                8604: '/fbcc/material',
                                // 8605: '/fbcc/material/ad',
                                8607: '/fbcc/material/bm',
                                8620: '/fbcc/material/bm',
                            };
                            ViewUI.Modal.warning({
                                closable: true,
                                title: router.app.$t('warningText'),
                                content: msg,
                                okText: router.app.$t('fbcc.addMNow'),
                                cancelText: router.app.$t('cancelText'),
                                onOk: () => {
                                    router.push(hrefArr[data.code]);
                                },
                                onCancel: () => {
                                    // router.push(hrefArr[data.code]);
                                },
                            });
                            return Promise.reject(data);
                        }

                        if (data.code === 4504 || data.code === 4006) {
                            if (router.app.$route.name && !isNotLoginPage(router.app.$route.name)) {
                                if (!showLogoutMessage) {
                                    showLogoutMessage = true;
                                    ViewUI.Message.error({
                                        background: true,
                                        closable: true,
                                        content: msg,
                                        duration: 6,
                                    });
                                }
                                clearLoginStatus();
                                const query: any = {
                                    url: '',
                                };
                                if (router.app.$route.name !== 'home') {
                                    query.url = escape(window.location.href);
                                }
                                window.location.href = `/login?url=${query.url}`;
                            }
                        } else {
                            // Message 抛出异常
                            ViewUI.Message.error({
                                background: true,
                                closable: true,
                                content: msg,
                                duration: 6,
                            });
                        }
                        return Promise.reject(data);
                }
            },
            (error: any) => {
                if (error.response && error.response.status) {
                    const { status } = error.response;
                    if (status > 499 && status < 600) {
                        ViewUI.Modal.error({
                            title: router.app.$t('base.tipText'),
                            render(h: any) {
                                return h(
                                    'div',
                                    {
                                        attrs: {
                                            class: 'modal-block',
                                        },
                                    },
                                    [
                                        h('p', `ㄟ(▔▽▔)ㄏ ${router.app.$t('errorCode.error')}`),
                                        h('p', `${router.app.$t('errorCode.error1')}(${status})`),
                                    ],
                                );
                            },
                        });

                        // 未知错误码上报
                        if (this.source === 'api') {
                            const _data: any = {
                                status,
                                url,
                            };
                            try {
                                _data.error = error.response.toString();
                                _data.response = error.response;
                            } catch (e) {
                                console.error(e);
                            }
                            router.app.$log('WEB_NETWORK', 'CODE_UNKNOWN_STATUS', { data: _data });
                        }
                    }
                }

                // 超时 网络错误的提示
                if (
                    error.code === 'ECONNABORTED' &&
                    error.message.includes('timeout') &&
                    !opt.disabledNetworkErrTip
                ) {
                    // 超时判断
                    // ViewUI.Modal.error({
                    //     title: router.app.$t('base.tipText'),
                    //     content: router.app.$t('errorCode.error2'),
                    // });
                    networkReport.count += 1;
                    networkReport.msg = error.message || 'timeout';
                    networkReport.url = url;
                    reportNetworkErr();
                }
                this.destroy(url);
                return Promise.reject(error);
            },
        );
    }

    // 处理 options
    public processOptions(options: any) {
        let projectId = '';
        let userId = '';
        if (router.app.$store && router.app.$store.state) {
            projectId = router.app.$store.state.user.projectId;
            userId = router.app.$store.state.user.userId;
        }
        if (!projectId) {
            return false;
        }

        if (options.method === 'post') {
            options.data = options.data || {};
            if (!options.data.project_id) {
                options.data.project_id = projectId;
            }
        } else {
            options.params = options.params || {};
            if (!options.params.project_id) {
                options.params.project_id = projectId;
            }
        }
        options.params = options.params || {};
        options.params._hya_ = userId;
        options.params._xma_ = projectId;
        options.params._ta_ = Date.now();
        if (this.source === 'ws') {
            options.params.project_id = projectId;
            const token = localRead(config.tokenKey);
            if (options.method === 'post') {
                options.data = options.data || {};
                options.data.cpl = token;
            } else {
                options.params.cpl = token;
            }
        }

        // 日志请求增加签名
        if (this.source === 'log' || options.url.indexOf('client/log/log') > -1 || options.url.indexOf('client/log/batch-log') > -1) {
            options.params.plugin_sign = genSign(options.params, SECRET);
        }

        return true;
    }

    public request<T>(options: any) {
        if (options.cancelDuplicateRequest) {
            // 是否取消重复请求
            const cancel = this.pendingRequests[options.url];
            if (cancel) { cancel(); }
        }

        this.processOptions(options);

        const instance = axios.create();
        instance.defaults.timeout = 75 * 1000;
        const opt = Object.assign(this.getInsideConfig(options), options);
        opt.cancelToken = new CancelToken((c) => {
            this.pendingRequests[opt.url] = c;
        });
        this.interceptors(instance, opt);
        const request = instance(opt) as HttpReq<T>;
        request.cancel = this.pendingRequests[opt.url];
        return request;
    }

    public upload<T>(options: any) {
        const instance = axios.create();
        instance.defaults.timeout = 181 * 1000;
        this.processOptions(options);
        const opt = Object.assign(this.getUploadConfig(options), options);
        let cancel;
        opt.cancelToken = new CancelToken((c) => {
            cancel = c;
        });
        this.interceptors(instance, opt);
        const upload = instance(opt) as HttpReq<T>;
        upload.cancel = cancel as unknown as Canceler;
        return upload;
    }
}
export default HttpRequest;
